<script lang="ts" setup>
import { computed } from 'vue';

import type { EmployeeCreateFormSchema } from '@modules/employees/submodules/create/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { EMPLOYEES_LIST_ROUTE_NAME } from '@modules/employees/constants';
import { TRANSLATION_GROUP_EMPLOYEES_CREATE } from '@modules/employees/submodules/create/constants';
import { useEmployeeCreateMutation } from '@modules/employees/submodules/create/mutations';
import { useEmployeeRolesListQuery } from '@modules/employees/submodules/create/queries';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

const { getTranslation } = useI18n();

const { data: rolesList, isLoading: isRolesLoading } = useEmployeeRolesListQuery();
const {
  mutate: onSubmit,
  isLoading: isCreateLoading,
  errors,
  clearError,
} = useEmployeeCreateMutation();

const rolesOptions = computed<IFormSelectInputOption<number>[]>(() => {
  if (!rolesList.value) {
    return [];
  }

  return rolesList.value?.roles.map((item) => ({
    value: item.id,
    label: `${item.name}`,
  }));
});

const formSchema = computed<FormSchema<EmployeeCreateFormSchema>>(() => ({
  name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_CREATE, 'NAME').value,
    required: true,
  },
  email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_CREATE, 'EMAIL').value,
    required: true,
  },
  role_id: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_EMPLOYEES_CREATE, 'ROLE').value,
    required: true,
    options: rolesOptions.value,
  },
}));
</script>

<template>
  <VCard v-data-qa-auto-id="'EmployeeCreatePage'"
    :title="$getTranslation(TRANSLATION_GROUP_EMPLOYEES_CREATE, 'MAIN_TITLE')"
    type="form"
  >
    <VLoader
      :loading="isRolesLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_EMPLOYEES_CREATE, 'CREATE')"
        :custom-errors="errors"
        :is-loading="isCreateLoading"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_EMPLOYEES_CREATE, 'BACK')"
            :to="{ name: EMPLOYEES_LIST_ROUTE_NAME }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
